import {
  Button,
  Checkbox,
  FormRow,
  Input,
  Modal,
  Spacer,
  Spinner,
  Stack,
  Text
} from '@efast_public/fjd-component-library';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { BehApiRoutes, behClient } from '../api/beh-api';
import {
  IAvailableRolesDto,
  IInviteUserRequest,
  IOrgUnitRoleDto
} from '../api/models/Einstellungen/userRoleDto';
import { useNotification } from '../shared/notification/useNotification';
import {
  ErrorType,
  userConflictErrorMsg
} from '../shared/utils/Error/ErrorCode';
import { getMandant } from '../shared/utils/mandant';

interface UserAndRoleManagementInviteModalProps {
  readonly onClose: () => void;
}

export function UserAndRoleManagementInviteModalSSO({
  onClose
}: UserAndRoleManagementInviteModalProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { notifyUser, notifyUserFromRequest } = useNotification();
  const [email, setEmail] = useState('');
  const [selectedSystemRoles, setSelectedSystemRoles] = useState<string[]>([]);
  const [selectedOrgUnitRoles, setSelectedOrgUnitRoles] = useState<
    IOrgUnitRoleDto[]
  >([]);

  const { data: availableRoles } = useQuery<IAvailableRolesDto>({
    queryKey: [BehApiRoutes.availableRoles()]
  });

  const { mutateAsync: inviteUser, isPending: isInviting } = useMutation({
    mutationFn: (userRequest: IInviteUserRequest) =>
      notifyUserFromRequest(
        () => behClient.post(BehApiRoutes.inviteUser(), userRequest),
        {
          successMessage: 'Einladung wurde erfolgreich versendet!',
          errorMessage: 'Einladung konnte nicht versendet werden.',
          errorIdHandler: {
            [ErrorType.BEHOERDENCLIENT_PROBLEMS_USER_CONFLICT]: () => {
              notifyUser({ intent: 'error', title: userConflictErrorMsg });
            }
          }
        }
      ),
    onSuccess: () => {
      onClose();
      queryClient.invalidateQueries({
        queryKey: [BehApiRoutes.userWithAuthorizations__(0, 10000)]
      });
    }
  });

  const handleToggleSystemRole = (roleName: string, checked: boolean) => {
    if (checked) {
      setSelectedSystemRoles((prev) => [...prev, roleName]);
    } else {
      setSelectedSystemRoles((prev) => prev.filter((r) => r !== roleName));
    }
  };

  const handleToggleOrgUnitRole = (
    orgUnitName: string,
    roleName: string,
    checked: boolean
  ) => {
    setSelectedOrgUnitRoles((prev) => {
      const existing = prev.find((o) => o.orgUnitName === orgUnitName);
      if (checked) {
        if (existing) {
          return prev.map((o) =>
            o.orgUnitName === orgUnitName
              ? { ...o, roles: [...o.roles, roleName] }
              : o
          );
        }
        return [...prev, { orgUnitName, roles: [roleName] }];
      } else {
        if (existing) {
          const newRoles = existing.roles.filter((r) => r !== roleName);
          if (newRoles.length === 0) {
            return prev.filter((o) => o.orgUnitName !== orgUnitName);
          }
          return prev.map((o) =>
            o.orgUnitName === orgUnitName ? { ...o, roles: newRoles } : o
          );
        }
        return prev;
      }
    });
  };

  const isOrgUnitRoleSelected = (orgUnitName: string, roleName: string) => {
    const orgUnit = selectedOrgUnitRoles.find(
      (o) => o.orgUnitName === orgUnitName
    );
    return orgUnit?.roles.includes(roleName) ?? false;
  };

  const handleInvite = async () => {
    await inviteUser({
      email,
      systemRoles: selectedSystemRoles,
      orgUnitRoles: selectedOrgUnitRoles.filter((o) => o.roles.length > 0),
      redirectUri: `${window.location.origin}/${getMandant()}`
    }).catch(() => {});
  };

  const isValidEmail = (emailValue: string) => {
    return /\S+@\S+\.\S+/.test(emailValue);
  };

  const hasAnyRoleSelected =
    selectedSystemRoles.length > 0 ||
    selectedOrgUnitRoles.some((o) => o.roles.length > 0);

  const isFormValid = () => {
    return email !== '' && isValidEmail(email) && hasAnyRoleSelected;
  };

  return (
    <Modal open onClose={onClose}>
      <Modal.Header id="UserAndRoleManagementInviteModalSSO-header">
        {t('usersAndGroup.addUserButton')}
      </Modal.Header>

      <Modal.Content>
        <Stack spacing="l">
          <FormRow label="E-Mail">
            <Input
              id="emailToInvite"
              type="email"
              onChange={(event) => setEmail(event.target.value)}
              value={email}
              placeholder="example@abc.com"
              disabled={isInviting}
            />
          </FormRow>

          <Spacer />

          <Stack direction="column" spacing="l">
            {availableRoles && availableRoles.systemRoles.length > 0 && (
              <FormRow label="Systemrollen">
                <Stack direction="column" spacing="s">
                  {availableRoles.systemRoles.map((role) => (
                    <Checkbox
                      key={role}
                      id={`invite-system-role-${role}`}
                      label={role}
                      checked={selectedSystemRoles.includes(role)}
                      onChange={(e) =>
                        handleToggleSystemRole(role, e.currentTarget.checked)
                      }
                      disabled={isInviting}
                    />
                  ))}
                </Stack>
              </FormRow>
            )}

            {availableRoles &&
              availableRoles.orgUnitRoles
                .sort((a, b) => a.orgUnitName.localeCompare(b.orgUnitName))
                .map((orgUnit) => (
                  <FormRow
                    key={orgUnit.orgUnitName}
                    label={
                      orgUnit.orgUnitName.charAt(0).toUpperCase() +
                      orgUnit.orgUnitName.slice(1)
                    }
                  >
                    <Stack direction="column" spacing="s">
                      {orgUnit.roles.map((role) => (
                        <Checkbox
                          key={`${orgUnit.orgUnitName}-${role}`}
                          id={`invite-orgunit-role-${orgUnit.orgUnitName}-${role}`}
                          label={role}
                          checked={isOrgUnitRoleSelected(
                            orgUnit.orgUnitName,
                            role
                          )}
                          onChange={(e) =>
                            handleToggleOrgUnitRole(
                              orgUnit.orgUnitName,
                              role,
                              e.currentTarget.checked
                            )
                          }
                          disabled={isInviting}
                        />
                      ))}
                    </Stack>
                  </FormRow>
                ))}
          </Stack>

          {!isValidEmail(email) && email !== '' && (
            <Text variant="error">
              Bitte geben Sie eine gültige E-Mail-Adresse ein
            </Text>
          )}
        </Stack>
      </Modal.Content>

      <Modal.Footer>
        <Stack direction="row" spacing="m" justify="end">
          <Button
            variant="primary-text"
            onClick={onClose}
            disabled={isInviting}
          >
            Abbrechen
          </Button>
          <Button
            variant="primary"
            onClick={handleInvite}
            disabled={!isFormValid() || isInviting}
          >
            {isInviting ? <Spinner size="sm" /> : 'Einladung versenden'}
          </Button>
        </Stack>
      </Modal.Footer>
    </Modal>
  );
}
